<template>
    <div class="listener-login">
        <div class="listener-login__bg listener-login__bg--top"></div>
        <div class="listener-login__bg listener-login__bg--bottom"></div>

        <div class="listener-login__content">
            <section class="listener-login__hero">
                <div class="listener-login__eyebrow">{{ $tt('Music') }}</div>
                <h1 class="listener-login__title">{{ $tt('回到你的聆听现场') }}</h1>
                <p class="listener-login__sub">{{ $tt('登录后继续播放、搜索和整理你喜欢的声音。') }}</p>
            </section>

            <section class="listener-login__card">
                <div class="listener-login__brand">
                    <div>
                        <div class="listener-login__brand-kicker">{{ $tt('Listener') }}</div>
                        <div class="listener-login__brand-title">{{ $tt('登录到您的账户') }}</div>
                    </div>
                </div>

                <form class="listener-login__form" @submit.prevent="$emit('submit')">
                    <div v-if="isAppMode" class="listener-login__field">
                        <div class="listener-login__label-row">
                            <label for="listener-api-url" class="listener-login__label">{{ $tt('服务器地址') }}</label>
                            <button
                                type="button"
                                class="listener-login__ghost"
                                :disabled="testing"
                                @click="$emit('test-connection')"
                            >
                                {{ testing ? $tt('测试中...') : $tt('测试连接') }}
                            </button>
                        </div>
                        <div class="listener-login__history-wrap">
                            <input
                                id="listener-api-url"
                                type="text"
                                :value="apiUrl"
                                :placeholder="$tt('请输入服务器地址，例如：http://192.168.1.100:8005')"
                                class="listener-login__input"
                                @input="$emit('update:api-url', $event.target.value)"
                                @focus="$emit('focus-history')"
                                @blur="$emit('blur-history')"
                            />
                            <div
                                v-if="showHistory && urlHistory.length > 0"
                                class="listener-login__history"
                            >
                                <button
                                    v-for="(url, index) in urlHistory"
                                    :key="index"
                                    type="button"
                                    class="listener-login__history-item"
                                    @mousedown.prevent="$emit('select-history', url)"
                                >
                                    {{ url }}
                                </button>
                            </div>
                        </div>
                        <p
                            v-if="connectionStatus.message"
                            :class="['listener-login__status', connectionStatus.success ? 'is-success' : 'is-error']"
                        >
                            {{ connectionStatus.message }}
                        </p>
                    </div>

                    <div class="listener-login__field">
                        <label for="listener-username" class="listener-login__label">{{ $tt('用户名') }}</label>
                        <input
                            id="listener-username"
                            type="text"
                            autocomplete="username"
                            :value="username"
                            :placeholder="$tt('请输入用户名')"
                            class="listener-login__input"
                            @input="$emit('update:username', $event.target.value)"
                            @keydown.enter="$emit('submit')"
                        />
                    </div>

                    <div class="listener-login__field">
                        <div class="listener-login__label-row">
                            <label for="listener-password" class="listener-login__label">{{ $tt('密码') }}</label>
                            <button type="button" class="listener-login__link" @click="$emit('forgot-password')">
                                {{ $tt('忘记密码？') }}
                            </button>
                        </div>
                        <input
                            id="listener-password"
                            type="password"
                            autocomplete="current-password"
                            :value="password"
                            :placeholder="$tt('请输入密码')"
                            class="listener-login__input"
                            @input="$emit('update:password', $event.target.value)"
                            @keydown.enter="$emit('submit')"
                        />
                    </div>

                    <button type="submit" class="listener-login__submit">
                        {{ $tt('登录') }}
                    </button>
                    <button
                        v-for="provider in visibleLoginProviders"
                        :key="provider.id"
                        type="button"
                        class="listener-login__submit"
                        @click="$emit('social-login', provider.id)"
                    >
                        {{ $tt('使用 {provider} 登录', { provider: provider.label }) }}
                    </button>
                </form>

                <p class="listener-login__footer">
                    {{ $tt('还没有账号？') }} {{ $tt('请联系管理员') }}
                </p>
            </section>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ListenerLogin',
    props: {
        username: {
            type: String,
            default: ''
        },
        password: {
            type: String,
            default: ''
        },
        isAppMode: {
            type: Boolean,
            default: false
        },
        apiUrl: {
            type: String,
            default: ''
        },
        testing: {
            type: Boolean,
            default: false
        },
        connectionStatus: {
            type: Object,
            default: () => ({
                success: false,
                message: ''
            })
        },
        showHistory: {
            type: Boolean,
            default: false
        },
        urlHistory: {
            type: Array,
            default: () => []
        },
        loginProviders: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        visibleLoginProviders() {
            return this.isAppMode ? [] : this.loginProviders
        }
    }
}
</script>

<style scoped>
.listener-login {
    position: relative;
    min-height: 100vh;
    min-height: 100dvh;
    padding: 1.4rem 1rem 1.8rem;
    background: linear-gradient(180deg, #f7f6f3 0%, #eef3ff 46%, #f7f7f7 100%);
    overflow-y: auto;
}

.listener-login__content {
    position: relative;
    z-index: 1;
    width: min(100%, 28rem);
    min-height: calc(100dvh - 3.2rem);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.listener-login__bg {
    position: fixed;
    z-index: 0;
    pointer-events: none;
    filter: blur(12px);
}

.listener-login__bg--top {
    top: -5rem;
    right: -4rem;
    width: 13rem;
    height: 13rem;
    border-radius: 2.5rem;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.18), rgba(59, 130, 246, 0));
}

.listener-login__bg--bottom {
    left: -4rem;
    bottom: 4rem;
    width: 12rem;
    height: 12rem;
    border-radius: 999px;
    background: radial-gradient(circle, rgba(244, 114, 182, 0.14), rgba(244, 114, 182, 0));
}

.listener-login__hero {
    padding: 0.5rem 0.2rem 1.15rem;
}

.listener-login__eyebrow {
    font-size: 0.86rem;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.04em;
}

.listener-login__title {
    margin-top: 0.25rem;
    font-size: 2.2rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-login__sub {
    margin-top: 0.7rem;
    max-width: 22rem;
    font-size: 0.9rem;
    line-height: 1.6;
    color: #64748b;
}

.listener-login__card {
    padding: 1.2rem;
    border-radius: 1.8rem;
    background:
        radial-gradient(circle at top right, rgba(191, 219, 254, 0.28), transparent 34%),
        rgba(255, 255, 255, 0.82);
    backdrop-filter: blur(18px);
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.14);
}

.listener-login__brand {
    display: flex;
    align-items: center;
    gap: 0.9rem;
}

.listener-login__logo {
    width: 2.9rem;
    height: 2.9rem;
    border-radius: 1rem;
    object-fit: cover;
    box-shadow: 0 12px 24px rgba(148, 163, 184, 0.18);
}

.listener-login__brand-kicker {
    font-size: 0.76rem;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.04em;
}

.listener-login__brand-title {
    margin-top: 0.14rem;
    font-size: 1.1rem;
    font-weight: 800;
    color: #111827;
}

.listener-login__form {
    margin-top: 1.15rem;
}

.listener-login__field + .listener-login__field {
    margin-top: 1rem;
}

.listener-login__label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.listener-login__label {
    display: block;
    margin-bottom: 0.45rem;
    font-size: 0.82rem;
    font-weight: 700;
    color: #475569;
}

.listener-login__ghost,
.listener-login__link {
    border: 0;
    background: transparent;
    font-size: 0.78rem;
    font-weight: 700;
}

.listener-login__ghost {
    color: #111827;
}

.listener-login__ghost:disabled {
    opacity: 0.45;
}

.listener-login__link {
    color: #64748b;
}

.listener-login__input {
    width: 100%;
    border: 0;
    border-radius: 1.1rem;
    padding: 0.95rem 1rem;
    background: rgba(255, 255, 255, 0.9);
    color: #111827;
    box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.9);
    outline: none;
}

.listener-login__input:focus {
    box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.9), 0 0 0 4px rgba(226, 232, 240, 0.6);
}

.listener-login__history-wrap {
    position: relative;
}

.listener-login__history {
    position: absolute;
    top: calc(100% + 0.35rem);
    left: 0;
    right: 0;
    max-height: 12rem;
    overflow-y: auto;
    padding: 0.35rem;
    border-radius: 1rem;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.18);
}

.listener-login__history-item {
    width: 100%;
    border: 0;
    background: transparent;
    padding: 0.78rem 0.8rem;
    border-radius: 0.85rem;
    text-align: left;
    color: #334155;
}

.listener-login__history-item + .listener-login__history-item {
    margin-top: 0.2rem;
}

.listener-login__status {
    margin-top: 0.45rem;
    font-size: 0.78rem;
    line-height: 1.5;
}

.listener-login__status.is-success {
    color: #0f766e;
}

.listener-login__status.is-error {
    color: #dc2626;
}

.listener-login__submit {
    width: 100%;
    margin-top: 1.2rem;
    border: 0;
    border-radius: 1.2rem;
    padding: 0.95rem 1rem;
    background: #111827;
    color: #fff;
    font-size: 0.95rem;
    font-weight: 800;
    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.18);
}

.listener-login__footer {
    margin-top: 1rem;
    font-size: 0.8rem;
    line-height: 1.6;
    color: #64748b;
    text-align: center;
}
</style>
